import { getLoginRes, setLoginRes } from "@/api";
import { useEffect, useRef, useState } from "react";
import { customToast } from "../../Toast";

interface IframeModalProps {
  open: boolean;
  onClose: () => void;
  iframeSrc: string;
  onStatusUpdate?: (status: string) => void;
  onAccountOpeningResult?: (result: {
    success: boolean;
    accountNumber?: string;
    status: string;
    error?: string;
  }) => void;
}

const heightMap = {
  full: "h-screen",
  large: "h-[90vh]",
  medium: "h-[80vh]",
};
export const IframeModal: React.FC<IframeModalProps> = ({
  open,
  onClose,
  iframeSrc,
  onStatusUpdate,
  onAccountOpeningResult,
}) => {
  const [loading, setLoading] = useState(true);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const height = "large";

  const targetOrigin = (() => {
    try {
      return iframeSrc ? new URL(iframeSrc).origin : "*";
    } catch {
      return "*";
    }
  })();

  useEffect(() => {
    if (iframeSrc) {
      setLoading(true);
    }
  }, [iframeSrc]);

  const dispatchAuthToken = () => {
    const tokenResString = getLoginRes();

    if (!tokenResString) {
      return "";
    }
    try {
      const tokenRes = JSON.parse(tokenResString);

      const message = {
        type: "AUTH_TOKEN",
        payload: {
          accessToken: tokenRes.accessToken,
          refreshToken: tokenRes.refreshToken,
        },
      };

      if (iframeRef.current && iframeRef.current.contentWindow) {
        iframeRef.current.contentWindow.postMessage(message, targetOrigin);
      }

      return tokenRes.accessToken;
    } catch {
      return "";
    }
  };

  const handleIframeLoaded = () => {
    setLoading(false);
    dispatchAuthToken();
  };

  // Listen for messages from the iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (targetOrigin !== "*" && event.origin !== targetOrigin) return;
      const { type, payload } = event.data;

      if (type === "CLOSE_AUTH_TOKEN") {
        onClose();
      } else if (type === "TOKEN_REFRESHED") {
        // wealthierai_web refreshed its own token — update Sootchy's cookie
        if (payload?.accessToken && payload?.refreshToken) {
          setLoginRes(payload);
        }
      } else if (type === "STATUS_UPDATE") {
        onStatusUpdate?.(payload?.status);
      } else if (type === "ACCOUNT_OPENING_RESULT") {
        onAccountOpeningResult?.(payload);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [onAccountOpeningResult, onClose, onStatusUpdate, targetOrigin]);

  // Forward Sootchy token refreshes to the iframe
  useEffect(() => {
    const handleReAuthenticate = (event: Event) => {
      if (event instanceof CustomEvent) {
        const { accessToken, refreshToken } = (event as CustomEvent).detail;

        if (accessToken && refreshToken) {
          if (iframeRef.current?.contentWindow) {
            iframeRef.current.contentWindow.postMessage(
              {
                type: "TOKEN_REFRESHED",
                payload: { accessToken, refreshToken },
              },
              targetOrigin,
            );
          }
        }
      }
    };

    window.addEventListener("re-authenticate", handleReAuthenticate);
    return () =>
      window.removeEventListener("re-authenticate", handleReAuthenticate);
  }, [targetOrigin]);

  if (!open) return null;

  return (
    <div className="fixed inset-0  flex items-center justify-center z-[9999]  bg-black/40 p-4 sm:p-6">
      <div className="absolute inset-0" onClick={onClose} />

      <div
        className={`relative w-full max-w-[1200px] ${heightMap[height]} rounded-lg relative bg-white `}
      >
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white z-[100]">
            <div className="w-10 h-10 border-4 border-gray-300 border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}
        <iframe
          ref={iframeRef}
          src={iframeSrc}
          onLoad={() => handleIframeLoaded()}
          onError={() => {
            customToast("Unable to load iframe", "error");
          }}
          className="
            w-full
            h-full
            bg-transparent
          "
          title="Embedded Modal"
        />
      </div>
    </div>
  );
};
